import { supabase } from "@/lib/supabase";
import { generateUniqueFilename } from "@/utils/image-utils";
import { BUCKET_NAME } from "@/lib/constants";
import { PreviewData, CropSettings, SearchOptions, SearchResult } from "@/lib/types";

interface CreatePreviewResult {
  success: boolean;
  previewId?: string;
  error?: string;
}

interface UpdatePreviewImageResult {
  success: boolean;
  newAssetPath?: string;
  error?: string;
}

export const uploadImageAndCreatePreview = async (
  image: File,
  modelUuid: string,
  options: {
    isSharable?: boolean;
    subPreviewId?: string;
    projectId?: string;
    cropSettings?: CropSettings;
    tags?: string[];
  } = {}
): Promise<CreatePreviewResult> => {
  try {
    // Get the current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Create a unique file path
    const fileName = generateUniqueFilename(image.name);
    const filePath = `images/${fileName}`;    // Save reference to database with user_id for RLS
    const { data: insertData, error: insertError } = await supabase
      .from('previews')
      .insert({
        name: image.name,
        model_uuid: modelUuid,
        asset_path: filePath,
        is_sharable: options.isSharable ?? false,
        sub_preview_id: options.subPreviewId ?? null,
        project_id: options.projectId ?? null,
        user_id: user.id, // Add user_id for RLS policy
        crop_settings: options.cropSettings ?? null,
        tags: options.tags ?? [],
      })
      .select('id');

    if (insertError) throw insertError;

    // Upload image to Supabase Storage
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, image, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      throw error;
    }

    // Return the created preview ID
    return {
      success: true,
      previewId: insertData?.[0]?.id
    };
  } catch (error) {
    console.error("Upload error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const updatePreviewImage = async (
  image: File,
  previewId: string,
  oldAssetPath: string,
  cropSettings?: CropSettings
): Promise<UpdatePreviewImageResult> => {
  try {
    // Create a unique file path
    const fileName = generateUniqueFilename(image.name);
    const newFilePath = `images/${fileName}`;

    // Upload new image to Supabase Storage

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(newFilePath, image, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;
    console.log("updatePreviewImage", cropSettings);

    // Update database record with new asset path and crop settings
    const { error: updateError } = await supabase
      .from('previews')
      .update({
        asset_path: newFilePath,
        crop_settings: cropSettings || null
      })
      .eq('id', previewId);
    if (updateError) throw updateError;

    // Delete old image from storage
    const { error: deleteError } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([oldAssetPath]);

    if (deleteError) {
      console.warn("Failed to delete old image:", deleteError);
      // Continue even if deletion fails
    }

    return {
      success: true,
      newAssetPath: newFilePath
    };
  } catch (error) {
    console.error("Update preview image error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const updatePreviewCropSettings = async (
  previewId: string,
  cropSettings: CropSettings
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Update only the crop settings in the database
    const { error: updateError } = await supabase
      .from('previews')
      .update({
        crop_settings: cropSettings
      })
      .eq('id', previewId);

    if (updateError) throw updateError;

    return {
      success: true
    };
  } catch (error) {
    console.error("Update crop settings error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const compatible_previews = async (model_uuid: string): Promise<PreviewData[]> => {
  try {
    // First get the compatible sub-model UUID from the available_models table
    const { data: modelData, error: modelError } = await supabase
      .from('available_models')
      .select('compatible_sub_model')
      .eq('uuid', model_uuid)
      .single();

    if (modelError) throw modelError;

    if (!modelData?.compatible_sub_model) {
      return []; // No compatible sub-model found
    }

    // Now fetch all previews created with the compatible sub-model
    const { data, error } = await supabase
      .from('previews')
      .select('*,model:available_models(name)')
      .eq('model_uuid', modelData.compatible_sub_model)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data as PreviewData[];
  } catch (error) {
    console.error("Error fetching compatible previews:", error);
    return [];
  }
}

// Tag-related service functions
export const updatePreviewTags = async (
  previewId: string,
  tags: string[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('previews')
      .update({ tags })
      .eq('id', previewId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Update tags error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const searchPreviewsByTags = async (
  tags: string[],
  userId?: string
): Promise<PreviewData[]> => {
  try {
    let query = supabase
      .from('previews')
      .select('*,model:available_models(name),project:projects(id,name)')
      .order('created_at', { ascending: false });

    // Filter by user if provided
    if (userId) {
      query = query.eq('user_id', userId);
    }

    // Filter by tags using array overlap operator
    if (tags.length > 0) {
      query = query.overlaps('tags', tags);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data as PreviewData[];
  } catch (error) {
    console.error("Error searching previews by tags:", error);
    return [];
  }
};

export const getAllUserTags = async (userId?: string): Promise<string[]> => {
  try {
    let query = supabase
      .from('previews')
      .select('tags');

    // Filter by user if provided
    if (userId) {
      query = query.eq('user_id', userId);
    }

    // Only get previews that have tags
    query = query.not('tags', 'is', null);

    const { data, error } = await query;

    if (error) throw error;

    // Flatten all tags and get unique values
    const allTags = data
      ?.flatMap(preview => preview.tags || [])
      .filter((tag, index, array) => array.indexOf(tag) === index)
      .sort() || [];

    return allTags;
  } catch (error) {
    console.error("Error fetching user tags:", error);
    return [];
  }
};

export const getPopularTags = async (limit = 10): Promise<Array<{ tag: string; count: number }>> => {
  try {
    // This is a more complex query - we'll use a simpler approach
    const { data, error } = await supabase
      .from('previews')
      .select('tags')
      .not('tags', 'is', null);

    if (error) throw error;

    // Count tag frequency
    const tagCounts: Record<string, number> = {};
    data?.forEach(preview => {
      preview.tags?.forEach((tag: string) => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });

    // Sort by count and return top tags
    return Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  } catch (error) {
    console.error("Error fetching popular tags:", error);
    return [];
  }
};

// Enhanced search function with multiple filters
export const searchPreviews = async (
  options: SearchOptions,
  userId?: string
): Promise<SearchResult<PreviewData>> => {
  try {
    const {
      filters = {},
      sortBy = 'created_at',
      sortOrder = 'desc',
      page = 0,
      pageSize = 10
    } = options;

    let query = supabase
      .from('previews')
      .select('*,model:available_models(name),project:projects(id,name)', { count: 'exact' });

    // Filter by user if provided
    if (userId) {
      query = query.eq('user_id', userId);
    }

    // Apply filters
    if (filters.name) {
      query = query.ilike('name', `%${filters.name}%`);
    }

    if (filters.tags && filters.tags.length > 0) {
      query = query.overlaps('tags', filters.tags);
    }

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.isSharable !== undefined) {
      query = query.eq('is_sharable', filters.isSharable);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const start = page * pageSize;
    const end = start + pageSize - 1;
    query = query.range(start, end);

    const { data, error, count } = await query;

    if (error) throw error;

    const total = count || 0;
    const hasMore = (page + 1) * pageSize < total;

    return {
      data: (data as PreviewData[]) || [],
      total,
      hasMore
    };
  } catch (error) {
    console.error("Error searching previews:", error);
    return {
      data: [],
      total: 0,
      hasMore: false
    };
  }
};

// Get all user projects for filter dropdown
export const getUserProjectsForFilter = async (userId?: string): Promise<Array<{ id: string; name: string }>> => {
  try {
    let query = supabase
      .from('projects')
      .select('id, name')
      .eq('is_archived', false)
      .order('name');

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error("Error fetching user projects:", error);
    return [];
  }
};
